/**
 * 需要想定 (単日) の小売の内訳 (`GetDemandForecastRetailerBreakdown`) を読み、内訳欄の表示状態と
 * グループタブの選択を返す hook。
 *
 * ページ本体 (`useGetDemandForecastDaily`) とは独立した query で、120 秒ごとの再取得には乗せない
 * (内訳は BigQuery を 1 query 走らせるため。staleTime を長くし refetchInterval を持たない)。
 * 失敗は内訳欄だけで扱い、ページ全体のエラーにしない。
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { getDemandForecastRetailerBreakdown } from '@ebalance/proto/bffuser/v1/demand_forecast_service-DemandForecastService_connectquery'
import {
  GetDemandForecastRetailerBreakdownRequestSchema,
  type GetDemandForecastRetailerBreakdownResponse,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import {
  BREAKDOWN_TOTAL_TAB,
  type RetailerBreakdownView,
  toRetailerBreakdownView,
} from '~/pages/DemandForecastDaily/models/retailerBreakdownView'

const BREAKDOWN_STALE_TIME_MS = 10 * 60_000

type Params = {
  caseId: string | undefined
  areaCode: AreaCode | undefined
  targetDate: ProtoDate | undefined
  /** 対象が小売の時だけ渡す。undefined なら読まず `hidden`。 */
  retailerBusinessCd: string | undefined
  /** ページ本体と同じ過去実績の日付 (日付チップの並び)。 */
  pastActualDates: ProtoDate[]
  /** page 応答が返した、この小売の想定モード。 */
  pageHasGroupForecast: boolean | undefined
}

export function useRetailerBreakdown({
  caseId,
  areaCode,
  targetDate,
  retailerBusinessCd,
  pastActualDates,
  pageHasGroupForecast,
}: Params): RetailerBreakdownView {
  const enabled =
    caseId !== undefined &&
    caseId !== '' &&
    areaCode !== undefined &&
    targetDate !== undefined &&
    retailerBusinessCd !== undefined

  // 過去日を除いた「同じ小売」の同一性。過去日を足し引きして読み直す間はこの小売の直前の
  // 応答でタブを出し続け、読み直しの失敗では直前の応答が hidden だったかで黙るかを決める。
  const retailerKey = JSON.stringify({
    caseId,
    areaCode,
    targetDate,
    retailerBusinessCd,
  })
  const lastSuccess = useRef<
    | { retailerKey: string; data: GetDemandForecastRetailerBreakdownResponse }
    | undefined
  >(undefined)

  const result = useQuery(
    getDemandForecastRetailerBreakdown,
    create(GetDemandForecastRetailerBreakdownRequestSchema, {
      caseId: caseId ?? '',
      areaCode: areaCode ?? 0,
      targetDate,
      retailerBusinessCd: retailerBusinessCd ?? '',
      pastActualDates,
    }),
    {
      enabled,
      staleTime: BREAKDOWN_STALE_TIME_MS,
      placeholderData: () =>
        lastSuccess.current?.retailerKey === retailerKey
          ? lastSuccess.current.data
          : undefined,
      // 内訳の失敗で編集中のページごと全画面エラーにしない。
      throwOnError: false,
    },
  )

  // useGetDemandForecastDaily と同じく「直前の値」を render 中に覚える。placeholder は覚えない。
  if (result.data !== undefined && !result.isPlaceholderData) {
    lastSuccess.current = { retailerKey, data: result.data }
  }

  let view: RetailerBreakdownView
  if (!enabled) view = { kind: 'hidden' }
  // 再取得の失敗では React Query が前回の data も残すので、data より先に error を見る
  // (古いグループ実績を失敗表示なしで出し続けない)。
  else if (result.error !== null) {
    const isUnimplemented =
      result.error instanceof ConnectError &&
      result.error.code === Code.Unimplemented
    const lastWasHidden =
      lastSuccess.current?.retailerKey === retailerKey &&
      toRetailerBreakdownView(lastSuccess.current.data).kind === 'hidden'
    view =
      isUnimplemented || lastWasHidden ? { kind: 'hidden' } : { kind: 'error' }
  } else if (result.data !== undefined)
    view = toRetailerBreakdownView(result.data)
  else view = { kind: 'loading' }

  // page は定期再取得される一方、内訳は BigQuery を避けるため長く cache する。
  // 同じ小売の想定モードが食い違った時に限り、モードごとに 1 回読み直す。
  // loading/error は再試行せず、同じ不一致応答が続いても polling しない。
  const modeMismatch =
    pageHasGroupForecast !== undefined &&
    ((pageHasGroupForecast && view.kind === 'direct') ||
      (!pageHasGroupForecast && view.kind === 'byGroup'))
  const mismatchKey =
    modeMismatch && pageHasGroupForecast !== undefined
      ? `${retailerKey}:${pageHasGroupForecast ? 'byGroup' : 'direct'}`
      : undefined
  const lastRefetchedMismatchKey = useRef<string | undefined>(undefined)
  const { isFetching, refetch } = result
  useEffect(() => {
    if (mismatchKey === undefined) {
      lastRefetchedMismatchKey.current = undefined
      return
    }
    if (lastRefetchedMismatchKey.current === mismatchKey || isFetching) return
    lastRefetchedMismatchKey.current = mismatchKey
    void refetch()
  }, [isFetching, mismatchKey, refetch])

  return view
}

/**
 * グループタブの選択。編集対象 (小売) が替わったら合計タブへ戻す。対象が小売で
 * なくなった時も戻すので、同じ小売を選び直しても合計から始まる。
 */
export function useBreakdownTabSelection(
  retailerBusinessCd: string | undefined,
): [string, (tabKey: string) => void] {
  const [selection, setSelection] = useState({
    retailerBusinessCd,
    tabKey: BREAKDOWN_TOTAL_TAB,
  })
  if (selection.retailerBusinessCd !== retailerBusinessCd) {
    setSelection({ retailerBusinessCd, tabKey: BREAKDOWN_TOTAL_TAB })
  }
  const select = useCallback(
    (tabKey: string) => {
      setSelection({ retailerBusinessCd, tabKey })
    },
    [retailerBusinessCd],
  )
  return [selection.tabKey, select]
}
